import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { IoIosArrowDown, IoMdClose } from "react-icons/io";
import { RxHamburgerMenu } from "react-icons/rx";
import { useContactStore } from "store";
import { routeNames } from "utils";

interface DropdownProps {
  isOpen: boolean;
}

interface MenuItemProps {
  active?: boolean;
}

interface NavProps {
  transparent: boolean;
}

const Nav = styled(motion.nav)<NavProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 2rem;
  background: ${(props) =>
    props.transparent
      ? "rgba(255, 255, 255, 0.85)"
      : "rgba(255, 255, 255, 0.15)"};
  backdrop-filter: blur(8px);
  box-shadow: ${(props) =>
    props.transparent
      ? "0 4px 30px rgba(0, 0, 0, 0.1)"
      : "0 2px 15px rgba(0, 0, 0, 0.05)"};
  font-family: "Manrope", sans-serif;
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
`;

const LogoContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const Logo = styled(motion.img)`
  height: 32px;
`;

const MenuItems = styled.div<DropdownProps>`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenuContainer = styled(motion.div)`
  position: absolute;
  top: 70px;
  left: 0;
  right: 0;
  background-color: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  overflow: hidden;
  padding: 1rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MenuItemLink = styled(motion(Link))<MenuItemProps>`
  color: #000;
  text-decoration: none;
  font-size: 14px;
  font-weight: 400;
  position: relative;
  transition: color 0.2s;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    color: #56ccf2;
  }

  ${(props) =>
    props.active &&
    `
    color: #56CCF2;
  `}
`;

const MenuItem = styled(motion.div)<MenuItemProps>`
  color: #000;
  text-decoration: none;
  font-size: 14px;
  font-weight: 400;
  position: relative;
  transition: color 0.2s;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    color: #56ccf2;
  }

  ${(props) =>
    props.active &&
    `
    color: #56CCF2;
  `}
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownIndicator = styled(motion.span)`
  margin-left: 5px;
  font-size: 10px;
  margin-top: 3px;
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: white;
  min-width: 160px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 0.5rem 0;
  z-index: 1001;
  margin-top: 0.5rem;
  overflow: hidden;

  @media (max-width: 768px) {
    position: static;
    box-shadow: none;
    padding: 0.5rem;
    margin-top: 0.5rem;
    width: 100%;
  }
`;

const DropdownItemLink = styled(motion(Link))`
  display: block;
  padding: 0.5rem 1rem;
  color: #000000;
  text-decoration: none;
  font-size: 14px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
    color: #5575f7;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    align-items: stretch;
    gap: 1rem;
    margin-top: 0.5rem;
  }
`;

const LoginButton = styled(motion(Link))`
  background: none;
  border: none;
  color: #333;
  font-family: "Manrope", sans-serif;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  padding: 0.5rem 1rem;
  text-decoration: none;
  text-align: center;

  &:hover {
    color: #5575f7;
  }
`;

const TrialButton = styled(motion(Link))`
  background-color: #5575f7;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 0.75rem 1.5rem;
  font-family: "Manrope", sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;
  text-decoration: none;
  text-align: center;
  display: block;

  &:hover {
    background-color: #5575f7;
  }
`;

const LanguageSelector = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  font-size: 14px;
`;

const MenuToggle = styled(motion.div)`
  display: none;
  cursor: pointer;
  font-size: 24px;
  color: #333;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenuItemLink = styled(motion(Link))<MenuItemProps>`
  color: #333;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
  display: block;

  &:hover {
    color: #5575f7;
  }

  ${(props) =>
    props.active &&
    `
    color: #5575F7;
  `}
`;

const MobileMenuItem = styled(motion.div)<MenuItemProps>`
  color: #333;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
  display: block;

  &:hover {
    color: #5575f7;
  }

  ${(props) =>
    props.active &&
    `
    color: #5575F7;
  `}
`;

export const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState<boolean>(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState<boolean>(false);
  const [isTransparent, setIsTransparent] = useState<boolean>(true);

  const navigate = useNavigate();
  const location = useLocation();
  const resourcesRef = useRef<HTMLDivElement>(null);
  const languageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (scrollPosition > 50) {
        setIsTransparent(false);
      } else {
        setIsTransparent(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resourcesRef.current &&
        !resourcesRef.current.contains(event.target as Node)
      ) {
        setIsResourcesOpen(false);
      }
      if (
        languageRef.current &&
        !languageRef.current.contains(event.target as Node)
      ) {
        setIsLanguageOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const languages = ["English", "Español", "Français"];
  const resourcesItems = [{ title: "Guide for Registering", link: "/guide" }];

  const navVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const logoVariants = {
    initial: { opacity: 0, x: -20 },
    animate: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
      },
    },
    hover: {
      scale: 1.05,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10,
      },
    },
  };

  const menuItemVariants = {
    hover: {
      scale: 1.05,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10,
      },
    },
    tap: { scale: 0.95 },
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10,
      },
    },
    tap: { scale: 0.95 },
  };

  const dropdownVariants = {
    hidden: {
      opacity: 0,
      y: -10,
      height: 0,
    },
    visible: {
      opacity: 1,
      y: 0,
      height: "auto",
      transition: {
        duration: 0.3,
        staggerChildren: 0.05,
      },
    },
    exit: {
      opacity: 0,
      y: -10,
      height: 0,
      transition: {
        duration: 0.2,
      },
    },
  };

  const dropdownItemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.2 },
    },
  };

  const mobileMenuVariants = {
    hidden: {
      height: 0,
      opacity: 0,
    },
    visible: {
      height: "auto",
      opacity: 1,
      transition: {
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
    exit: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
        when: "afterChildren",
      },
    },
  };

  const mobileMenuItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.2 },
    },
    exit: {
      opacity: 0,
      y: 20,
      transition: { duration: 0.1 },
    },
  };

  const dropdownIconVariants = {
    closed: { rotate: 0 },
    open: { rotate: 180, transition: { duration: 0.3 } },
  };

  // Check if a path is active
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <Nav
      initial="hidden"
      animate="visible"
      variants={navVariants}
      transparent={isTransparent}
    >
      <LogoContainer onClick={() => navigate(routeNames.home)}>
        <Logo
          src="/assets/logo.png"
          alt="compellingcandidature"
          variants={logoVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
        />
      </LogoContainer>

      <MenuToggle
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        {isMenuOpen ? <IoMdClose /> : <RxHamburgerMenu />}
      </MenuToggle>

      {/* Desktop menu */}
      <MenuItems isOpen={false}>
        <MenuItemLink
          to={routeNames.home}
          variants={menuItemVariants}
          whileHover="hover"
          whileTap="tap"
          active={isActive("/")}
        >
          Home
        </MenuItemLink>
        <MenuItemLink
          to={routeNames.about}
          variants={menuItemVariants}
          whileHover="hover"
          whileTap="tap"
          active={isActive(routeNames.about)}
        >
          About us
        </MenuItemLink>

        <DropdownContainer ref={resourcesRef}>
          <MenuItem
            onClick={() => setIsResourcesOpen(!isResourcesOpen)}
            variants={menuItemVariants}
            whileHover="hover"
            whileTap="tap"
            active={isResourcesOpen}
          >
            Resources
            <DropdownIndicator
              variants={dropdownIconVariants}
              animate={isResourcesOpen ? "open" : "closed"}
            >
              <IoIosArrowDown style={{ fontSize: "14px" }} />
            </DropdownIndicator>
          </MenuItem>

          <AnimatePresence>
            {isResourcesOpen && (
              <DropdownMenu
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {resourcesItems.map((item, index) => (
                  <DropdownItemLink
                    key={index}
                    to={item.link}
                    variants={dropdownItemVariants}
                    whileHover={{ x: 5, color: "#00c2cb" }}
                  >
                    {item.title}
                  </DropdownItemLink>
                ))}
              </DropdownMenu>
            )}
          </AnimatePresence>
        </DropdownContainer>

        <MenuItem
          onClick={() => useContactStore.getState().openModal()}
          variants={menuItemVariants}
          whileHover="hover"
          whileTap="tap"
        >
          Contact us
        </MenuItem>

        <ButtonContainer>
          <LoginButton
            to={"#"}
            onClick={() => {
              window.location.href = routeNames.login
            }}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            Login
          </LoginButton>

          {/* <TrialButton
            to="/signup"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            Start your 3-day free trial
          </TrialButton> */}

          {/* <DropdownContainer ref={languageRef}>
            <LanguageSelector
              onClick={() => setIsLanguageOpen(!isLanguageOpen)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              English
              <DropdownIndicator
                variants={dropdownIconVariants}
                animate={isLanguageOpen ? "open" : "closed"}
              >
                <IoIosArrowDown style={{ fontSize: "16px" }} />
              </DropdownIndicator>
            </LanguageSelector>

            <AnimatePresence>
              {isLanguageOpen && (
                <DropdownMenu
                  variants={dropdownVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {languages.map((lang, index) => (
                    <DropdownItemLink
                      key={index}
                      to="#"
                      variants={dropdownItemVariants}
                      whileHover={{ x: 5, color: "#00c2cb" }}
                    >
                      {lang}
                    </DropdownItemLink>
                  ))}
                </DropdownMenu>
              )}
            </AnimatePresence>
          </DropdownContainer> */}
        </ButtonContainer>
      </MenuItems>

      {/* Mobile menu with animations */}
      <AnimatePresence>
        {isMenuOpen && (
          <MobileMenuContainer
            variants={mobileMenuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={() => setIsMenuOpen(false)}
          >
            <MobileMenuItemLink
              to={routeNames.home}
              variants={mobileMenuItemVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              active={isActive(routeNames.home)}
            >
              Home
            </MobileMenuItemLink>
            <MobileMenuItemLink
              to={routeNames.about}
              variants={mobileMenuItemVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              active={isActive(routeNames.about)}
            >
              About us
            </MobileMenuItemLink>
            <DropdownContainer>
              <MobileMenuItem
                onClick={() => setIsResourcesOpen(!isResourcesOpen)}
                variants={mobileMenuItemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                active={isResourcesOpen}
              >
                Resources
                <DropdownIndicator
                  variants={dropdownIconVariants}
                  animate={isResourcesOpen ? "open" : "closed"}
                  style={{ position: "absolute", right: "2rem" }}
                >
                  <IoIosArrowDown style={{ fontSize: "16px" }} />
                </DropdownIndicator>
              </MobileMenuItem>

              <AnimatePresence>
                {isResourcesOpen && (
                  <DropdownMenu
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    {resourcesItems.map((item, index) => (
                      <DropdownItemLink
                        key={index}
                        to={item.link}
                        variants={dropdownItemVariants}
                      >
                        {item.title}
                      </DropdownItemLink>
                    ))}
                  </DropdownMenu>
                )}
              </AnimatePresence>
            </DropdownContainer>

            <MobileMenuItem
              onClick={() => useContactStore.getState().openModal()}
              variants={mobileMenuItemVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              Contact us
            </MobileMenuItem>
            <MobileMenuItemLink
              to={"#"}
              variants={mobileMenuItemVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              active={isActive("/login")}
              onClick={() => {
                window.location.href = routeNames.login
              }}
            >
              Login
            </MobileMenuItemLink>
            {/* <TrialButton
              to={routeNames.signUp}
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              Start your 3-day free trial
            </TrialButton> */}
           {/*  <DropdownContainer>
              <MobileMenuItem
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                variants={mobileMenuItemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                English
                <DropdownIndicator
                  variants={dropdownIconVariants}
                  animate={isLanguageOpen ? "open" : "closed"}
                  style={{ position: "absolute", right: "2rem" }}
                >
                  <IoIosArrowDown style={{ fontSize: "16px" }} />
                </DropdownIndicator>
              </MobileMenuItem>

              <AnimatePresence>
                {isLanguageOpen && (
                  <DropdownMenu
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    {languages.map((lang, index) => (
                      <DropdownItemLink
                        key={index}
                        to="#"
                        variants={dropdownItemVariants}
                      >
                        {lang}
                      </DropdownItemLink>
                    ))}
                  </DropdownMenu>
                )}
              </AnimatePresence>
            </DropdownContainer> */}
          </MobileMenuContainer>
        )}
      </AnimatePresence>
    </Nav>
  );
};
